import {notifications} from '@mantine/notifications'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {useNavigate} from '@tanstack/react-router'

import {invalidateCustomerSavedLocations} from 'src/customers/saved-locations/invalidate-saved-locations'
import {setProjectDispatchYard} from 'src/dispatch/api'
import {clearDraft, saveCreatedMark} from 'src/jobs/create-job-storage'
import {describeJobCreationFailure} from 'src/jobs/create-job-submit'
import {jobKeys} from 'src/jobs/query-keys'
import type {CreateJobFormValues} from 'src/jobs/types'
import {createProject, createProjectJob} from 'src/projects/api'
import {projectKeys} from 'src/projects/query-keys'
import {quoteKeys} from 'src/quotes/query-keys'
import {workKeys} from 'src/work/query-keys'

import {type AddToOrder, toAddedJobInput, toNewOrderInput} from './job-entry-logic'

function useCreateOrderWithJob() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (values: CreateJobFormValues) => createProject(toNewOrderInput(values)),
        onSuccess: (_project, {quoteId, customerId}) => {
            void queryClient.invalidateQueries({queryKey: projectKeys.lists()})
            void queryClient.invalidateQueries({queryKey: jobKeys.all})
            void queryClient.invalidateQueries({queryKey: workKeys.all})
            if (quoteId) {
                void queryClient.invalidateQueries({queryKey: quoteKeys.detail(quoteId)})
                void queryClient.invalidateQueries({queryKey: quoteKeys.list()})
            }
            if (customerId) {
                invalidateCustomerSavedLocations(queryClient, customerId)
            }
        },
    })
}

/** Create a project job, setting the project dispatch yard first when requested. */
function useAddJobToOrder(projectId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({
            values,
            setOrderYard,
        }: {
            values: CreateJobFormValues
            setOrderYard: boolean
        }) => {
            if (setOrderYard && values.dispatchYardId) {
                await setProjectDispatchYard(projectId, values.dispatchYardId)
            }
            return createProjectJob(projectId, toAddedJobInput(values))
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({queryKey: projectKeys.detail(projectId)})
            void queryClient.invalidateQueries({queryKey: projectKeys.lists()})
            void queryClient.invalidateQueries({queryKey: jobKeys.all})
            void queryClient.invalidateQueries({queryKey: workKeys.all})
        },
    })
}

export function useSaveJobEntry({
    storageKey,
    order,
}: {
    storageKey: string
    order: AddToOrder | null
}) {
    const navigate = useNavigate()
    const createOrder = useCreateOrderWithJob()
    const addJob = useAddJobToOrder(order?.projectId ?? '')

    function showFailure(error: unknown) {
        notifications.show({...describeJobCreationFailure(error), color: 'red'})
    }

    function save(values: CreateJobFormValues) {
        if (order) {
            addJob.mutate(
                {values, setOrderYard: !order.dispatchYardId},
                {
                    onSuccess: (job) => {
                        clearDraft(storageKey)
                        saveCreatedMark(storageKey, {
                            label: `Job ${job.displayId}`,
                            projectId: order.projectId,
                            jobId: job.id,
                        })
                        void navigate({
                            to: '/jobs/create/done/$jobId',
                            params: {jobId: job.id},
                            search: {added: 1},
                        })
                    },
                    onError: showFailure,
                },
            )
            return
        }
        createOrder.mutate(values, {
            onSuccess: (project) => {
                const job = project.jobs[0]
                clearDraft(storageKey)
                saveCreatedMark(storageKey, {
                    label: `Job ${job?.displayId ?? values.jobName.trim()}`,
                    projectId: project.id,
                    jobId: job?.id ?? null,
                })
                void (job
                    ? navigate({
                          to: '/jobs/create/done/$jobId',
                          params: {jobId: job.id},
                          search: {},
                      })
                    : navigate({to: '/projects/$projectId', params: {projectId: project.id}}))
            },
            onError: showFailure,
        })
    }

    const error = createOrder.error ?? addJob.error
    return {
        save,
        isPending: createOrder.isPending || addJob.isPending,
        errorMessage: error ? describeJobCreationFailure(error).message : null,
    }
}
